import React, { useState } from "react";
import {
  Combobox,
  InputBase,
  Group,
  Text,
  Button,
  Paper,
  ActionIcon,
  Divider,
  Stack,
  Modal,
} from "@mantine/core";
import { useClient } from "../../context/ClientContext";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconX } from "@tabler/icons-react";
import ClientForm from "../clients/ClientForm";
import { formatPhoneNumber } from "../../utils/phoneUtils";

/**
 * @param {object} props
 * @param {any} props.value
 * @param {(id: string) => void} props.onChange
 * @param {any} [props.error]
 * @param {boolean} [props.disabled]
 * @param {string | null} [props.label]
 * @param {boolean} [props.required]
 * @param {string} [props.placeholder]
 * @param {import('react').ReactNode} [props.leftSection]
 * @param {string | number} [props.radius]
 * @param {string | number} [props.size]
 * @param {boolean} [props.pinCreate] - "+ Add a new client" pinned under the
 *   results, always visible (calendar booking drawer); otherwise the create
 *   button shows only in the empty / no-results state.
 */
function ClientLookup({
  value,
  onChange,
  error,
  disabled = false,
  // Optional presentation overrides (defaults preserve existing callers).
  label = "Client",
  required = true,
  placeholder = "Search for a client...",
  leftSection = null,
  radius,
  size,
  pinCreate = false,
}) {
  const [opened, setOpened] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { allClients, clients, searchQuery, setSearchQuery } = useClient();
  const [isCreatingNew, { open: openNewClient, close: closeNewClient }] =
    useDisclosure(false);

  const handleCreateNew = (newClient) => {
    closeNewClient();
    // Clear the search query when closing
    setSearchQuery("");

    // If a client was successfully created, select it
    if (onChange && newClient) {
      onChange(newClient.id);
      setOpened(false);
      setIsFocused(false);
    }
  };

  const handleClear = (e) => {
    if (disabled) return;
    e.stopPropagation();
    onChange("");
    setSearchQuery("");
  };

  const selectedClient = allClients.find((c) => c.id === value);

  const formatClientPhone = (client) => {
    if (!client.area_code || !client.phone_number) return "";
    return formatPhoneNumber(`${client.area_code}${client.phone_number}`);
  };

  const displayValue = selectedClient
    ? `${selectedClient.first_name} ${selectedClient.last_name} - ${formatClientPhone(selectedClient)}${selectedClient.email ? ` | ${selectedClient.email}` : ""}`
    : searchQuery;

  return (
    <>
      <Combobox
        onOptionSubmit={(val) => {
          if (disabled) return;
          onChange(val);
          setSearchQuery("");
          setOpened(false);
          setIsFocused(false);
        }}
        opened={disabled ? false : opened}
        onChange={disabled ? () => {} : setOpened}
        onFocus={() => !disabled && setIsFocused(true)}
        onBlur={() => !disabled && setIsFocused(false)}
      >
        <Combobox.Target>
          <InputBase
            label={label}
            required={required}
            error={error}
            disabled={disabled}
            placeholder={placeholder}
            leftSection={leftSection}
            radius={radius}
            size={size}
            onClick={() => !disabled && setOpened(true)}
            rightSection={
              disabled ? null : selectedClient ? (
                <ActionIcon size="sm" variant="subtle" onClick={handleClear}>
                  <IconX size={14} />
                </ActionIcon>
              ) : (
                <Combobox.Chevron />
              )
            }
            value={displayValue}
            onChange={(e) => {
              if (disabled) return;
              if (selectedClient) {
                onChange("");
              }
              setSearchQuery(e.target.value);
              setOpened(true);
              setIsFocused(true);
            }}
            styles={(theme) => ({
              input: {
                "&:focus": {
                  borderColor: theme.colors.teal[5],
                },
                ...(disabled && {
                  backgroundColor: theme.colors.gray[1],
                  color: theme.colors.gray[6],
                  cursor: "not-allowed",
                }),
              },
            })}
          />
        </Combobox.Target>

        {!disabled && isFocused && (
          <Combobox.Dropdown>
            <Combobox.Options mah={300} style={{ overflowY: "auto" }}>
              {!searchQuery && pinCreate ? (
                <Combobox.Empty>
                  <Text size="sm">
                    Search for a client by name, phone number or email
                  </Text>
                </Combobox.Empty>
              ) : !searchQuery ? (
                <Combobox.Empty>
                  <Stack gap="xs">
                    <Text size="sm">
                      Search for a client by name, phone number or email
                    </Text>
                    <Divider label="Or" />
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      onClick={openNewClient}
                    >
                      Create New Client
                    </Button>
                  </Stack>
                </Combobox.Empty>
              ) : clients.length > 0 ? (
                clients.map((client) => (
                  <Combobox.Option value={client.id} key={client.id}>
                    <Text size="sm">
                      {client.first_name} {client.last_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatClientPhone(client)}{" "}
                      {client.email ? `| ${client.email}` : ""}
                    </Text>
                  </Combobox.Option>
                ))
              ) : pinCreate ? (
                <Combobox.Empty>
                  <Text size="sm">No results found</Text>
                </Combobox.Empty>
              ) : (
                <Combobox.Empty>
                  <Group justify="space-between">
                    <Text size="sm">No results found</Text>
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      onClick={openNewClient}
                    >
                      Create New Client
                    </Button>
                  </Group>
                </Combobox.Empty>
              )}
            </Combobox.Options>
            {pinCreate && (
              <Combobox.Footer>
                <Button
                  variant="subtle"
                  color="teal"
                  size="sm"
                  fullWidth
                  justify="flex-start"
                  leftSection={<IconPlus size={15} />}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={openNewClient}
                >
                  Add a new client
                </Button>
              </Combobox.Footer>
            )}
          </Combobox.Dropdown>
        )}
      </Combobox>

      <Modal
        opened={disabled ? false : isCreatingNew}
        onClose={closeNewClient}
        title="Create New Client"
        size="lg"
        centered
      >
        <Paper p="md">
          <ClientForm onClose={handleCreateNew} showAddCardOption={false} />
        </Paper>
      </Modal>
    </>
  );
}

export default ClientLookup;
